import { batch } from 'solid-js';
import { persistantStore } from '../store/persistantStore';
import {
  selectedTime,
  setSelectedTime,
  timeWindow,
  setTimeWindow,
} from '../store/playbackStore';
import {
  selectedRacesTimeseries,
  setSelectedRacesTimeseries,
  selectedLegsTimeseries,
  setSelectedLegsTimeseries,
  selectedSources,
  setSelectedSources,
} from '../store/filterStore';
import { applyRouteFromShare, validateSharePage } from './shareApplyUtils';
import { debug } from './console';

export const EXPLORE_MAP_SHARE_PAGE_KEY = 'ExploreMap';
export const FLEET_MAP_SHARE_PAGE_KEY = 'FleetMap';

export interface MapShareState {
  selectedTime?: string;
  timeWindow?: number;
  selectedRaces?: string[];
  selectedLegs?: string[];
  selectedSources?: string[];
  selectedSourceId?: number;
  selectedSourceName?: string;
  /** Fleet map: reference boat NAME for VMG-delta timeline mode (empty = NONE). Persisted in overlay state. */
  referenceBoatName?: string;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
}

/** Overlay-state key holding the fleet reference boat name (mirrors MapContainer's `overlay_` persistence). */
const REFERENCE_BOAT_OVERLAY_KEY = 'referenceBoatName';

/** Read the persisted fleet reference boat name from persistent settings, falling back to localStorage. */
function readReferenceBoatName(): string {
  try {
    const overlays = persistantStore.mapOverlays?.();
    if (overlays && overlays[REFERENCE_BOAT_OVERLAY_KEY] !== undefined) {
      return String(overlays[REFERENCE_BOAT_OVERLAY_KEY] ?? '').trim();
    }
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(`overlay_${REFERENCE_BOAT_OVERLAY_KEY}`);
      if (stored !== null) return String(JSON.parse(stored) ?? '').trim();
    }
  } catch {
    /* ignore */
  }
  return '';
}

/** Persist the fleet reference boat name to both persistent settings and localStorage (matches MapContainer). */
function writeReferenceBoatName(name: string): void {
  const value = String(name ?? '').trim();
  try {
    // mapOverlays is typed Record<string, boolean> but MapContainer also stores string overlay
    // values (e.g. dataOverlayName) under the same store; cast to keep parity with that usage.
    const current = persistantStore.mapOverlays?.() || {};
    persistantStore.setMapOverlays?.({ ...current, [REFERENCE_BOAT_OVERLAY_KEY]: value } as unknown as Record<string, boolean>);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(`overlay_${REFERENCE_BOAT_OVERLAY_KEY}`, JSON.stringify(value));
    }
  } catch {
    /* ignore */
  }
}

function validIsoTime(value: Date): string | undefined {
  const ms = value.getTime();
  return Number.isFinite(ms) ? value.toISOString() : undefined;
}

export function buildExploreMapShareState(): Record<string, unknown> {
  const state: MapShareState = {
    selectedTime: validIsoTime(selectedTime()),
    timeWindow: timeWindow(),
    selectedRaces: selectedRacesTimeseries(),
    selectedLegs: selectedLegsTimeseries(),
    selectedSourceId: persistantStore.selectedSourceId(),
    selectedSourceName: persistantStore.selectedSourceName(),
  };
  return state as Record<string, unknown>;
}

export function buildFleetMapShareState(highlightedSourceIds?: Iterable<number>): Record<string, unknown> {
  const highlightedSources = highlightedSourceIds ? Array.from(highlightedSourceIds) : [];
  const state: MapShareState & { highlightedSourceIds?: number[] } = {
    selectedTime: validIsoTime(selectedTime()),
    timeWindow: timeWindow(),
    selectedRaces: selectedRacesTimeseries(),
    selectedLegs: selectedLegsTimeseries(),
    selectedSources: selectedSources(),
    referenceBoatName: readReferenceBoatName(),
    highlightedSourceIds: highlightedSources,
  };
  return state as Record<string, unknown>;
}

export function applyMapShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  options?: {
    sessionStorageKey?: string;
    setHighlightedSourceIds?: (ids: Set<number>) => void;
  }
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }

  applyRouteFromShare(obj);

  batch(() => {
    if (typeof obj.selectedTime === 'string') {
      const nextTime = new Date(obj.selectedTime);
      if (Number.isFinite(nextTime.getTime())) {
        setSelectedTime(nextTime, pageKey === FLEET_MAP_SHARE_PAGE_KEY ? 'fleetmap' : 'explore_map', true);
      }
    }

    if (typeof obj.timeWindow === 'number' && Number.isFinite(obj.timeWindow) && obj.timeWindow >= 0) {
      setTimeWindow(obj.timeWindow);
      if (options?.sessionStorageKey && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(options.sessionStorageKey, String(obj.timeWindow));
      }
    }

    if (Array.isArray(obj.selectedRaces)) {
      setSelectedRacesTimeseries(stringArray(obj.selectedRaces));
    }

    if (Array.isArray(obj.selectedLegs)) {
      setSelectedLegsTimeseries(stringArray(obj.selectedLegs));
    }

    if (Array.isArray(obj.selectedSources)) {
      setSelectedSources(stringArray(obj.selectedSources));
    }

    if (typeof obj.selectedSourceId === 'number' && Number.isFinite(obj.selectedSourceId)) {
      persistantStore.setSelectedSourceId(obj.selectedSourceId);
    }

    if (typeof obj.selectedSourceName === 'string') {
      persistantStore.setSelectedSourceName(obj.selectedSourceName);
    }

    if (typeof obj.referenceBoatName === 'string') {
      writeReferenceBoatName(obj.referenceBoatName);
    }

    if (Array.isArray(obj.highlightedSourceIds) && options?.setHighlightedSourceIds) {
      const ids = obj.highlightedSourceIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      options.setHighlightedSourceIds(new Set(ids));
    }
  });

  debug('[mapShareState] Applied map share state', {
    pageKey,
    selectedRaces: Array.isArray(obj.selectedRaces) ? obj.selectedRaces.length : 0,
    selectedSources: Array.isArray(obj.selectedSources) ? obj.selectedSources.length : 0,
    hasSelectedTime: typeof obj.selectedTime === 'string',
    timeWindow: obj.timeWindow,
  });

  return true;
}
